import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import * as R from 'ramda'
import type { PrimitiveTableColumn } from '@pbt/pbt-ui-components'
import { DateUtils, Defaults, NumberUtils } from '@pbt/pbt-ui-components'
import { ChevronDown, ChevronUp } from '@pbt/pbt-ui-components/src/icons'

import ExpandableTable from '~/components/common/lists/ExpandableTable'
import PrimitiveTableWithSearchHighlights from '~/components/common/lists/primitive-table/PrimitiveTableWithSearchHighlights'
import {
  FlagIcon,
  ThermometerIcon,
} from '~/components/elements/Icons/generated'
import {
  fetchInventoryOrders,
  fetchMoreInventoryOrders,
  getInventoryOrdersFilters,
  getInventoryOrdersIsFetchingList,
  getInventoryOrdersList,
  getInventoryOrdersSortings,
  getInventoryOrdersTotalCount,
  getMultipleInventoryOrders,
  setInventoryOrderFilters,
  setInventoryOrderSortings,
} from '~/store/duck/inventoryOrders'
import type { InventoryOrderForTable, TableFilter } from '~/types'
import { getUrlSearchParam } from '~/utils'

import InventoryTableCategoryFilter from '../catalog/InventoryTableCategoryFilter'
import DistributorCell from '../shipments/DistributorCell'
import { InventoryOrderCategoryCell } from '../shipments/InventoryOrderCategoryCell'
import ShipmentStatusFilter from '../shipments/ShipmentStatusFilter'
import ShipmentStatusLabel from '../shipments/ShipmentStatusLabel'
import { OrdersTableRow } from './OrdersTableRow'

interface OrdersComponentProps {
  headerButtons?: React.ReactNode
  headerTabs: React.ReactNode
}

const OrdersTableComponent = ({
  headerButtons,
  headerTabs,
}: OrdersComponentProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Admin'])
  const navigate = useNavigate()
  const location = useLocation()
  const search = getUrlSearchParam('query', location.search) || ''
  const filters = useSelector(getInventoryOrdersFilters)
  const sortings = useSelector(getInventoryOrdersSortings)
  const inventoryOrdersList = useSelector(getInventoryOrdersList)
  const totalCount = useSelector(getInventoryOrdersTotalCount)
  const isFetchingList = useSelector(getInventoryOrdersIsFetchingList)
  const inventoryOrders = useSelector(
    getMultipleInventoryOrders(inventoryOrdersList),
  )

  useEffect(() => {
    dispatch(
      fetchInventoryOrders(0, Defaults.INFINITE_LIST_BATCH_LOAD_COUNT, search),
    )
  }, [search, filters, sortings])

  const loadMoreItems = (startIndex: number, endIndex: number) => {
    dispatch(fetchMoreInventoryOrders(startIndex, endIndex, search))
  }

  const onApplyFilter = (filter: string, value: TableFilter) => {
    dispatch(setInventoryOrderFilters({ ...filters, [filter]: value }))
  }

  const onClearFilters = () => {
    dispatch(setInventoryOrderFilters({}))
  }

  const onApplySort = (sortKey: string, value: boolean) => {
    dispatch(
      setInventoryOrderSortings(
        sortings[sortKey]
          ? R.omit([sortKey], sortings)
          : { ...sortings, [sortKey]: value },
      ),
    )
  }

  const onClearSortings = () => {
    dispatch(setInventoryOrderSortings({}))
  }

  const isItemLoaded = useCallback(
    (index: number) => Boolean(inventoryOrders[index]),
    [inventoryOrders],
  )

  const renderOrderItemFlags = ({
    inventoryOrderFlags,
  }: InventoryOrderForTable) => (
    <Box color={(theme) => theme.colors.important}>
      {inventoryOrderFlags?.controlled && <FlagIcon />}
      {inventoryOrderFlags?.storeCold && <ThermometerIcon />}
    </Box>
  )

  const columns: PrimitiveTableColumn[] = [
    {
      label: t('Admin:CATALOG.ORDERS.ORDERED_DATE'),
      prop: ({ orderedDate }: { orderedDate: string }) =>
        DateUtils.formatDate(orderedDate) || '-',
      width: 2,
      sorting: 'creationDate',
      SortingIcon: sortings.creationDate ? ChevronUp : ChevronDown,
    },
    {
      label: t('Admin:CATALOG.ORDERS.PURCHASE_ORDER_NUMBER'),
      prop: 'orderNumber',
      width: 1,
    },
    {
      label: t('Admin:CATALOG.ORDERS.SUPPLIER'),
      prop: DistributorCell,
      width: 2,
    },
    {
      label: t('Admin:CATALOG.ORDERS.ORDERED_BY'),
      prop: 'orderedByEmail',
      width: 2,
    },
    {
      label: t('Common:CATEGORY'),
      prop: InventoryOrderCategoryCell,
      filter: 'category',
      FilterComponent: InventoryTableCategoryFilter,
      width: 3,
    },
    {
      label: t('Admin:CATALOG.ORDERS.LINES_RECEIVED'),
      prop: ({ linesReceived, totalLines }: InventoryOrderForTable) =>
        totalLines
          ? `${linesReceived} / ${totalLines} | ${NumberUtils.toPercentFormat(linesReceived / totalLines)}`
          : '-',
      width: 2,
    },
    {
      filter: 'status',
      FilterComponent: ShipmentStatusFilter,
      label: t('Common:STATUS'),
      noTypography: true,
      prop: ShipmentStatusLabel,
      width: 2,
    },
    {
      label: t('Common:FLAGS'),
      prop: renderOrderItemFlags,
      width: 1,
    },
    {
      label: t('Admin:CATALOG.ORDERS.LAST_RECEIVED'),
      prop: ({ lastReceivedDate }: { lastReceivedDate: string }) =>
        lastReceivedDate
          ? DateUtils.formatDateWithHours(lastReceivedDate)
          : '-',
      width: 2,
    },
  ]

  return (
    <ExpandableTable
      hasSelectedFilters={!R.isEmpty(filters)}
      headerButtons={headerButtons}
      headerTabs={headerTabs}
      isLoading={isFetchingList}
      list={inventoryOrders}
      searchTerm={search}
      title={t('Common:INVENTORY')}
      onSelected={(id) => navigate(id)}
    >
      <PrimitiveTableWithSearchHighlights
        RowComponent={OrdersTableRow}
        columns={columns}
        filters={filters}
        isItemLoaded={isItemLoaded}
        loadMoreItems={loadMoreItems}
        sortings={sortings}
        totalCount={totalCount}
        onApplyFilter={onApplyFilter}
        onApplySorting={onApplySort}
        onClearFilters={onClearFilters}
        onClearSortingItem={onClearSortings}
      />
    </ExpandableTable>
  )
}

export default OrdersTableComponent
